// features/cx/outstanding/hooks.ts

import { useSmartQuery, useSmartMutation } from '@shared/api/queries/hooks';
import { QueryKeyFactory } from '@shared/api/queries/queryKeyFactory';
import { useState, useCallback } from 'react';
import { outstandingApi } from './api';
import { toast } from "@shared/hooks/use-toast";

import {
  OutstandingBillsFilters,
  CreatePaymentAgreementPayload,
  PaymentAgreementFilters,
} from './types';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { queryClient } from '@shared/api/queries/queryClient';
import { downloadCSV } from '@shared/utils/utils';

// ================================
// OUTSTANDING BILLS HOOKS
// ================================

/**
 * Get outstanding bills list with pagination and search
 */
export const useOutstandingBills = (filters: OutstandingBillsFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.list(filters),
    () => outstandingApi.getOutstandingBills(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

export const useOutstandingBillsExport = (filters: OutstandingBillsFilters) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.exportList(filters),
    async () => {
      setIsExporting(true);
      try {
        const blob = await outstandingApi.exportOutstandingBills(filters);
        
        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });
        
        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export outstanding bills: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    }
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

/**
 * Get outstanding summary statistics
 */
export const useOutstandingSummary = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.summary(remoteUtilityId),
    () => outstandingApi.getOutstandingSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

/**
 * Get account details by ID
 */
export const useAccountDetails = (params: { remoteUtilityId: any; consumerNumber: any }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.accountDetails(params.consumerNumber),
    () => outstandingApi.getAccountDetails(params),
    {
      enabled: !!params.consumerNumber && !!params.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

// ================================
// PAYMENT AGREEMENT HOOKS
// ================================

/**
 * Get payment agreement detail
 */
export const usePaymentAgreementDetail = (params: {
  remoteUtilityId: number;
  remoteBillId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.paymentAgreementDetail(params.remoteUtilityId, params.remoteBillId),
    () => outstandingApi.getPaymentAgreementDetail(params),
    {
      enabled: !!params.remoteUtilityId && !!params.remoteBillId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Get payment agreements list
 */
export const usePaymentAgreements = (filters: PaymentAgreementFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.paymentAgreements(filters.remoteUtilityId, filters.remoteBillId),
    () => outstandingApi.getPaymentAgreements(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Create payment agreement
 */
export const useCreatePaymentAgreement = (remoteUtilityId: number, remoteBillId: number) => {
  return useSmartMutation(
    (payload: CreatePaymentAgreementPayload) => outstandingApi.createPaymentAgreement(payload),
    {
      onSuccess: () => {
        toast({
          title: "Payment agreement created successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.paymentAgreements(remoteUtilityId, remoteBillId),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.paymentAgreementDetail(remoteUtilityId, remoteBillId),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.list(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.summary(remoteUtilityId),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to create payment agreement!",
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Update payment agreement
 */
export const useUpdatePaymentAgreement = (remoteUtilityId: number, remoteBillId: number, agreementId: number) => {
  return useSmartMutation(
    (payload: Partial<CreatePaymentAgreementPayload>) => 
      outstandingApi.updatePaymentAgreement(agreementId, payload), 
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.outstanding.paymentAgreements(remoteUtilityId, remoteBillId),
        QueryKeyFactory.module.cx.outstanding.paymentAgreementDetail(remoteUtilityId, remoteBillId),
        QueryKeyFactory.module.cx.outstanding.list(),
        QueryKeyFactory.module.cx.outstanding.summary(remoteUtilityId),
      ],
      onSuccess: () => {
        toast({
          title: "Payment agreement updated successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to update payment agreement!",
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Delete payment agreement
 */
export const useDeletePaymentAgreement = (remoteUtilityId: number, remoteBillId: number) => {
  return useSmartMutation(
    (agreementId: number) => outstandingApi.deletePaymentAgreement({agreementId}),
    {
      onSuccess: () => {
        toast({
          title: "Payment agreement deleted successfully!",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.paymentAgreements(remoteUtilityId, remoteBillId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.paymentAgreementDetail(remoteUtilityId, remoteBillId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.list(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.summary(remoteUtilityId),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to delete payment agreement!",
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Send reminder
 */
export const useSendReminder = () => {
  return useSmartMutation(
    (payload: any) => outstandingApi.sendReminder(payload),
    {
      onSuccess: () => {
        toast({
          title: "Reminder sent successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to send reminder!",
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Custom hook for payment agreement filters and pagination
 */
export const usePaymentAgreementFilters = (initialFilters: PaymentAgreementFilters) => {
  const [filters, setFilters] = useState<PaymentAgreementFilters>({
    page: 1,
    limit: 50,
    ...initialFilters,
  });

  const updateFilters = useCallback((newFilters: Partial<PaymentAgreementFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: newFilters.page || 1,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      page: 1,
      limit: 50,
      ...initialFilters,
    });
  }, [initialFilters]);

  const nextPage = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      page: (prev.page || 1) + 1,
    }));
  }, []);

  const prevPage = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      page: Math.max((prev.page || 1) - 1, 1),
    }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters(prev => ({
      ...prev,
      page: Math.max(page, 1),
    }));
  }, []);

  const queryResult = usePaymentAgreements(filters);

  return {
    filters,
    updateFilters,
    resetFilters,
    nextPage,
    prevPage,
    setPage,
    ...queryResult,
  };
};